/**
 * Utilitaire central de suivi des requêtes IA.
 * Permet au feed ONY du Dashboard de collecter toutes les requêtes IA
 * effectuées depuis n'importe quelle partie du site.
 */

const STORAGE_KEY = 'ony_request_flow';
const HISTORY_LIMIT = 30;
const DEDUP_WINDOW_MS = 5000;

const CHANNEL_INFO = {
  'nova-chat': { name: 'Ony Chat', color: '#8b5cf6' },
  'email-template': { name: 'Templates Email', color: '#3b82f6' },
  'weather-automation': { name: 'Météo IA', color: '#06b6d4' },
  'excel-ai': { name: 'Excel AI', color: '#22c55e' },
  'web-search': { name: 'Recherche Web', color: '#ec4899' },
  'content-generation': { name: 'Génération Créative', color: '#a855f7' },
  'document-analysis': { name: 'Analyse Documents', color: '#64748b' },
  'automations': { name: 'Automatisations IA', color: '#f59e0b' },
  'notes': { name: 'Notes IA', color: '#f59e0b' },
  'code-editor': { name: 'Code IA', color: '#a855f7' },
  'file-transform': { name: 'Transformation Fichier', color: '#22c55e' },
  'project-analyzer': { name: 'Analyseur Projet', color: '#64748b' },
  'npc': { name: 'NPC IA', color: '#ec4899' },
  'conversation-context': { name: 'Contexte Conversation', color: '#3b82f6' },
  'nova-actions': { name: 'Actions Nova', color: '#06b6d4' },
  'scene-analyzer': { name: 'Analyse Scène', color: '#64748b' },
  'generic': { name: 'IA', color: '#71d5f5' }
};

export function getChannelInfo(channelId) {
  return CHANNEL_INFO[channelId] || CHANNEL_INFO['generic'];
}

export function getAiRequestHistory() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

function saveHistory(arr) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(0, HISTORY_LIMIT)));
  } catch {
    // ignore
  }
}

function generateId(payload) {
  const ts = payload.timestamp || Date.now();
  const req = (payload.request || '').toString().slice(0, 80);
  const channel = payload.channelId || 'generic';
  return `${channel}::${req}::${ts}`;
}

function findExisting(arr, payload) {
  if (payload.id) {
    return arr.findIndex(i => i.id === payload.id);
  }
  const now = payload.timestamp || Date.now();
  const request = payload.request || '';
  const channelId = payload.channelId || 'generic';
  return arr.findIndex(i => {
    const sameRequest = (i.request || '') === request;
    const sameChannel = (i.channelId || 'generic') === channelId;
    const closeTime = Math.abs((i.timestamp || 0) - now) < DEDUP_WINDOW_MS;
    return sameRequest && sameChannel && closeTime;
  });
}

export function trackAiRequest(payload) {
  const channel = getChannelInfo(payload.channelId);
  const normalized = {
    id: payload.id || generateId(payload),
    channelId: payload.channelId || 'generic',
    channelName: payload.channelName || channel.name,
    channelColor: payload.channelColor || channel.color,
    request: payload.request || '',
    response: payload.response || null,
    specialContent: payload.specialContent || null,
    status: payload.status || 'pending',
    history: payload.history || null,
    context: payload.context || null,
    outputs: payload.outputs || null,
    error: payload.error || null,
    timestamp: payload.timestamp || Date.now()
  };

  const existing = getAiRequestHistory();
  const idx = findExisting(existing, payload);

  if (idx >= 0) {
    existing[idx] = { ...existing[idx], ...normalized };
  } else {
    existing.unshift(normalized);
  }

  const trimmed = existing.slice(0, HISTORY_LIMIT);
  saveHistory(trimmed);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ony-ai-request-tracked', { detail: normalized }));
  }
}

export function subscribeAiRequestHistory(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = (e) => callback(e.detail);
  window.addEventListener('ony-ai-request-tracked', handler);
  return () => window.removeEventListener('ony-ai-request-tracked', handler);
}
